import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { SEOHead } from '../components/site/SEOHead';
import { Lightbox } from '../components/site/Lightbox';
import api, { getFileUrl } from '../lib/api';

export default function Services() {
  const { slug } = useParams();
  return slug ? <ServiceDetail slug={slug} /> : <ServiceList />;
}

function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/services?active_only=true').then(r => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="services-page">
      <SEOHead title="Hizmetlerimiz" />
      <div className="relative py-16 md:py-24 bg-[#0f1419]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">Hizmetlerimiz</h1>
          <p className="text-gray-300 mt-2">Sunduğumuz hizmetler</p>
          <div className="mt-4 w-16 h-1 rounded-full bg-[hsl(var(--gold))]" />
        </div>
      </div>

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
          : services.length === 0 ? <div className="text-center py-20 text-muted-foreground">Henüz hizmet eklenmemiş</div>
          : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Link key={s.id} to={`/hizmet/${s.slug}`} className="group rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)] overflow-hidden hover:border-[hsl(var(--gold)/0.45)] transition-colors">
                <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                  {s.cover_image ? <img src={getFileUrl(s.cover_image)} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground">Görsel yok</div>}
                  <span className="absolute left-4 top-4 text-3xl font-heading font-semibold tracking-tight tabular-nums text-[hsl(var(--gold))]">{String(i+1).padStart(2,'0')}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-lg mb-1">{s.title}</h3>
                  {s.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{s.description}</p>}
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--gold))] group-hover:gap-2.5 transition-all">Detay <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>}
        </div>
      </section>
    </div>
  );
}

function ServiceDetail({ slug }) {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => { api.get(`/services/${slug}`).then(r => setService(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;
  if (!service) return <div className="min-h-screen flex flex-col items-center justify-center"><p className="text-muted-foreground mb-4">Hizmet bulunamadı</p><Link to="/hizmetlerimiz" className="text-[hsl(var(--gold))]">Hizmetlere dön</Link></div>;

  const allImages = [service.cover_image, ...(typeof service.images === 'string' ? JSON.parse(service.images || '[]') : (service.images || []))].filter(Boolean);

  return (
    <div data-testid="service-detail-page">
      <SEOHead title={service.title} description={service.description} />
      {/* Hero */}
      <div className="relative h-[40vh] md:h-[50vh] bg-[#0f1419]">
        {allImages.length > 0 && <>
          <img src={getFileUrl(allImages[currentImage])} alt={service.title} className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightboxIndex(currentImage)} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/30 to-transparent" />
          {allImages.length > 1 && <>
            <button onClick={() => setCurrentImage(prev => (prev - 1 + allImages.length) % allImages.length)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => setCurrentImage(prev => (prev + 1) % allImages.length)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white"><ChevronRight className="w-5 h-5" /></button>
          </>}
        </>}
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">{service.title}</h1>
            {service.description && <p className="text-gray-300 mt-2 text-lg">{service.description}</p>}
          </div>
        </div>
      </div>

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link to="/hizmetlerimiz" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"><ArrowLeft className="w-4 h-4" /> Hizmetlere Dön</Link>
          {service.content && <div className="prose max-w-none"><p className="text-sm md:text-base leading-relaxed text-muted-foreground whitespace-pre-line">{service.content}</p></div>}
          {allImages.length > 1 && (
            <div className="mt-12">
              <h3 className="font-heading text-xl font-semibold mb-6">Görseller</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setLightboxIndex(i)} className="group relative rounded-xl overflow-hidden aspect-[4/3] border-2 border-transparent hover:border-[hsl(var(--gold))] transition-colors" data-testid={`service-gallery-thumb-${i}`}>
                    <img src={getFileUrl(img)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Lightbox images={allImages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndexChange={setLightboxIndex} />
    </div>
  );
}
